var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { InlineSelect, Text } from '@ui';
import { useAttachmentFieldCommit } from '../hooks/useAttachmentFieldCommit';
/**
 * Inline-editable `Category` cell (E2E-28). `InlineSelect` is genuinely controlled (unlike
 * `InlineText`'s contentEditable): its displayed `value` never changes until the parent re-renders
 * with a new one, so — per the design's own "don't optimistically update" note — `value` here is
 * only ever the last server-confirmed category. A failed commit therefore reverts for free: the
 * displayed value simply never moved, so only the inline error needs surfacing.
 */
export function AttachmentCategoryCell({ item, buildingId, options, addCategoryLabel, onRequestAddCategory }) {
    const { commit } = useAttachmentFieldCommit(buildingId, item.id);
    const [categoryId, setCategoryId] = useState(item.categoryId);
    const [error, setError] = useState(null);
    // Refetch-clobber guard (InspectionStatusRenderer.tsx:52-55): a listing refetch replaces `item`
    // without remounting this cell — skip re-syncing while a commit is in flight. Read via a ref,
    // not a state dependency: this effect must only re-run when `item.categoryId` itself changes
    // (a genuine external update), never merely because our own commit below flips `isSaving` back
    // to false — React 17 does not batch state updates made from a Promise callback, so `isSaving`
    // flipping first would otherwise re-run this effect and reset `categoryId` from the still-stale
    // `item` prop before the very next line's `setCategoryId(result.row...)` lands.
    const isSavingRef = useRef(false);
    useEffect(() => {
        if (isSavingRef.current)
            return;
        setCategoryId(item.categoryId);
    }, [item.categoryId]);
    const handleChange = (nextCategoryId) => __awaiter(this, void 0, void 0, function* () {
        if (nextCategoryId === categoryId)
            return;
        isSavingRef.current = true;
        setError(null);
        const result = yield commit({ attachmentCategoryId: nextCategoryId });
        isSavingRef.current = false;
        if (result.success) {
            setCategoryId(result.row.attachmentCategoryId);
            setError(null);
        }
        else {
            setError(result.error);
        }
    });
    return (React.createElement("span", { className: "attachments-surface__category-cell", "data-action-cell": true },
        React.createElement(InlineSelect, { options: options.map((option) => ({ value: option.id, label: option.label })), value: categoryId, onChange: (value) => {
                void handleChange(value);
            }, showSearch: true, footerAction: addCategoryLabel && onRequestAddCategory
                ? {
                    label: addCategoryLabel,
                    onSelect: () => onRequestAddCategory((createdCategoryId) => {
                        void handleChange(createdCategoryId);
                    })
                }
                : undefined }),
        error && (React.createElement(Text, { variant: "body", role: "alert", className: "attachments-surface__field-error" }, error))));
}
